import { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import { useImportedPanelenStore } from '../stores/importedPanelenStore'

/**
 * Orion AI Context
 * 
 * Provides context awareness to the AI system:
 * - Current active project
 * - Current active quote
 * - Current cabinet configuration
 * - User's current view/state
 */
// Create context with default value to prevent errors when used outside provider
const defaultContextValue = {
  contextType: 'dashboard',
  activeProjectId: null,
  activeQuoteId: null,
  activeCabinetId: null,
  activeProject: null,
  activeQuote: null,
  aiContext: {},
  updateCabinetState: () => {}
}

const OrionAIContext = createContext(defaultContextValue)

export function useOrionAIContext() {
  const context = useContext(OrionAIContext)
  // Context will always have a value (either from provider or default)
  // This prevents "must be used within provider" errors
  return context || defaultContextValue
}

export function OrionAIProvider({ children }) {
  const location = useLocation()
  const params = useParams()

  // Detect current context from route
  const [contextType, setContextType] = useState('dashboard') // 'project', 'quote', 'cabinet', 'dashboard'
  const [activeProjectId, setActiveProjectId] = useState(null)
  const [activeQuoteId, setActiveQuoteId] = useState(null)
  const [activeCabinetId, setActiveCabinetId] = useState(null)

  // Fetch active project if project ID is in route
  const { data: activeProject } = useQuery({
    queryKey: ['project', params.projectId],
    queryFn: async () => {
      if (!params.projectId) return null
      const response = await api.get(`/projecten/${params.projectId}`)
      return response.data
    },
    enabled: !!params.projectId,
  })

  // Fetch active quote if quote ID is in route
  const { data: activeQuote } = useQuery({
    queryKey: ['offerte', params.offerteId],
    queryFn: async () => {
      if (!params.offerteId) return null
      const response = await api.get(`/offertes/${params.offerteId}`)
      return response.data
    },
    enabled: !!params.offerteId,
  })

  // Cross-module: WhatsApp receipt inbox – vul window.__orionRecentReceipts voor AI Guardian
  const { data: inboxReceipts = [] } = useQuery({
    queryKey: ['receipts', 'inbox'],
    queryFn: async () => {
      try {
        const res = await api.get('/receipts/inbox')
        return res.data || []
      } catch {
        return []
      }
    },
    staleTime: 60000, // 1 min
  })
  useEffect(() => {
    if (Array.isArray(inboxReceipts) && inboxReceipts.length > 0) {
      window.__orionRecentReceipts = inboxReceipts
    } else {
      window.__orionRecentReceipts = []
    }
  }, [inboxReceipts])

  // Cross-module: accounting software (voor export naar Yuki/Exact)
  const { data: financialSettings } = useQuery({
    queryKey: ['financial-settings'],
    queryFn: async () => {
      try {
        const response = await api.get('/settings/financials')
        return response.data
      } catch {
        return null
      }
    },
    staleTime: 60000, // 1 min cache
  })

  // Luister naar PDF import complete voor AI context update (Phase 2)
  const [recentImportVersion, setRecentImportVersion] = useState(0)
  const setImportedPanelen = useImportedPanelenStore((s) => s.setImportedPanelen)
  useEffect(() => {
    const handler = () => setRecentImportVersion((v) => v + 1)
    window.addEventListener('orion-pdf-import-complete', handler)
    return () => window.removeEventListener('orion-pdf-import-complete', handler)
  }, [])

  // Phase 2: Material Calculator handoff – vul store bij orion-pdf-panelen-ready
  useEffect(() => {
    const handler = (e) => {
      const { project_id, panelen } = e.detail || {}
      if (project_id != null && Array.isArray(panelen)) {
        setImportedPanelen(project_id, panelen)
      }
    }
    window.addEventListener('orion-pdf-panelen-ready', handler)
    return () => window.removeEventListener('orion-pdf-panelen-ready', handler)
  }, [setImportedPanelen])

  // Detect context from route
  useEffect(() => {
    const path = location.pathname

    if (path.includes('/projecten/') && params.projectId) {
      setContextType('project')
      setActiveProjectId(parseInt(params.projectId))
    } else if (path.includes('/offertes/') && params.offerteId) {
      setContextType('quote')
      setActiveQuoteId(parseInt(params.offerteId))
    } else if (path.includes('/kasten/') && params.kastId) {
      setContextType('cabinet')
      setActiveCabinetId(parseInt(params.kastId))
    } else {
      setContextType('dashboard')
      setActiveProjectId(null)
      setActiveQuoteId(null)
      setActiveCabinetId(null)
    }
  }, [location.pathname, params])

  // Build sanitized context object for AI
  const aiContext = useMemo(() => {
    const context = {
      type: contextType,
      route: location.pathname,
      timestamp: new Date().toISOString(),
    }

    // Add project context
    if (activeProject) {
      context.project = {
        id: activeProject.id,
        naam: activeProject.naam,
        klant_id: activeProject.klant_id,
        klant_naam: activeProject.klant?.naam,
        status: activeProject.status,
        // Don't include sensitive data
      }
    }

    // Add quote context
    if (activeQuote) {
      context.quote = {
        id: activeQuote.id,
        project_id: activeQuote.project_id,
        ruimte_id: activeQuote.ruimte_id,
        status: activeQuote.status,
        totaal_prijs: activeQuote.totaal_prijs,
        // Don't include full quote lines
      }
    }

    // Add cabinet context (if available in state)
    const cabinetState = window.__orionCabinetState
    if (cabinetState) {
      context.cabinet = {
        width_mm: cabinetState.width_mm,
        height_mm: cabinetState.height_mm,
        depth_mm: cabinetState.depth_mm,
        plate_material_id: cabinetState.plate_material_id,
        plate_thickness_mm: cabinetState.plate_thickness_mm || cabinetState.plaat_dikte_mm,
        edge_material_id: cabinetState.edge_material_id,
        edge_thickness_mm: cabinetState.edge_thickness_mm || cabinetState.rand_dikte_mm,
        front_style: cabinetState.front_style,
      }
    }

    // Cross-module: SketchUp sync status (van WebSocket of import)
    const sketchUpStatus = window.__orionSketchUpSyncStatus
    if (sketchUpStatus) {
      context.sketchup = {
        connected: sketchUpStatus.connected,
        lastSync: sketchUpStatus.lastSync,
        projectId: sketchUpStatus.projectId,
      }
    }

    // Cross-module: recent WhatsApp receipts (n8n bonnen-scan) – voor AI Guardian proactieve notificatie
    const recentReceipts = window.__orionRecentReceipts
    if (recentReceipts && Array.isArray(recentReceipts) && recentReceipts.length > 0) {
      context.whatsapp_receipts = {
        count: recentReceipts.length,
        receipts: recentReceipts.map((r) => ({
          id: r.id,
          supplier_name: r.supplier_name,
          total_amount: r.total_amount,
          created_at: r.created_at,
        })),
        lastUpload: recentReceipts[0]?.created_at,
      }
    }

    // Cross-module: geselecteerd boekhoudpakket (Yuki, Exact, etc.)
    if (financialSettings?.accounting_provider) {
      context.accounting = {
        provider: financialSettings.accounting_provider,
        configured: !!financialSettings.accounting_credentials,
      }
    }

    // Cross-module: recente PDF import (Phase 2 – AI Guardian handoff)
    // Strikte mapping op import_metadata.material_calculator_ingest schema
    const recentImport = window.__orionRecentImport
    if (recentImport && recentImport.project_id) {
      context.pdf_import = {
        project_id: recentImport.project_id,
        material_calculator_ingest: recentImport.material_calculator_ingest || [],
        mapped_count: recentImport.mapped_count ?? 0,
        unmapped_count: recentImport.unmapped_count ?? 0,
        unmapped_descriptions: recentImport.unmapped_descriptions || [],
        unmapped_items: recentImport.unmapped_items || [], // Items waar material_id null/ontbreekt
        imported_at: recentImport.imported_at,
      }
    }

    return context
  }, [contextType, location.pathname, activeProject, activeQuote, financialSettings, recentImportVersion])

  const value = {
    contextType,
    activeProjectId,
    activeQuoteId,
    activeCabinetId,
    activeProject,
    activeQuote,
    aiContext, // Sanitized context for AI
    updateCabinetState: (cabinetState) => {
      // Allow components to update cabinet state
      window.__orionCabinetState = cabinetState
    },
  }

  return (
    <OrionAIContext.Provider value={value}>
      {children}
    </OrionAIContext.Provider>
  )
}
